import React, { useMemo, useState } from 'react';
import { dayjs } from 'shared/utils/date';
import globalMessages from 'translations/messages';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from 'hooks/apollo';
import { Link } from 'react-router-dom';

import { FormattedMessage, FormattedNumber } from 'react-intl';
import { Badge, Input, Space } from 'antd';
import { DeleteFilled, LoadingOutlined } from '@ant-design/icons';

import LanguageTag from 'components/LanguageTag';
import SurveyGroupStatusTag from 'components/SurveyGroupStatusTag';
import Table from 'components/Table';
import Page from 'components/Page';

import { sum } from 'shared/constants/math';

import useGlobalContext from 'hooks/useGlobalContext';
import { renderCustomer, renderRelativeDate, renderTags, renderVersion } from 'components/Table/renderers';
import { uniqueArray } from 'shared/utils/unique';
import _ from 'lodash';
import { arraySorter, boolSorter } from 'components/Table/sorters';
import SurveyTypeTag from 'components/SurveyTypeTag';
import { getDistributionMethodsColumn } from 'components/SurveyDistributionTag';
import { SurveyType } from 'shared/prisma/types';
import { getDistributionMethods } from 'shared/utils/distribution';
import messages from './messages';

const GET_SURVEY_GROUPS = gql`
  query Surveys_getSurveyGroups {
    surveyGroups(includeFlaggedCommentsCount: true) {
      id
      title
      customer {
        id
        name
      }
      dataSource {
        id
      }
      surveys {
        id
        type
        title
        version
        primary
        language
        languageName
        viewCounts
        responseCounts
        firstResponse
        lastResponse
        flaggedCommentsCount
        distributions {
          id
          behaviorSetting
          container
          trigger
        }
        categories {
          id
          title
        }
      }
    }
  }
`;

const DELETE_SURVEY = gql`
  mutation Surveys_deleteSurvey($id: ID!) {
    deleteSurvey(id: $id)
  }
`;

const mapSurvey = (survey, group) => {
  const { id, title, distributions, language, languageName, viewCounts, responseCounts, flaggedCommentsCount, firstResponse, lastResponse, primary, type } = survey;
  const [viewCount] = viewCounts;
  const [responseCount] = responseCounts;

  let version;

  if (survey.version) {
    version = primary ? 'Primary' : 'Translation';
  }

  return {
    id,
    title,
    type,
    surveyId: survey.id,
    groupId: group.id,
    version: version || null,
    languages: [{ language, languageName }],
    viewCount: viewCount || null,
    responseCount: responseCount || null,
    flaggedCommentsCount: flaggedCommentsCount || null,
    firstResponse: firstResponse && dayjs(firstResponse),
    lastResponse: lastResponse && dayjs(lastResponse),
    distributions,
    distributionMethods: getDistributionMethods(distributions, type),
  };
};

const mapSurveyVersion = (survey, group) => {
  const { title, version } = survey;

  const surveys = group.surveys.filter(s => s.version === version).sort((a, b) => boolSorter(a.primary, b.primary)).map(s => mapSurvey(s, group));

  return {
    id: `primary-${survey.id}`,
    title,
    type: survey.type,
    surveyId: survey.id,
    groupId: group.id,
    version: version ? `v${version}` : null,
    primary: true,
    languages: uniqueArray(surveys.flatMap(s => s.languages), { prop: 'language' }).sort((a, b) => a.languageName?.localeCompare(b.languageName)),
    viewCount: sum(surveys, { valueSelector: s => s.viewCount }) || null,
    responseCount: sum(surveys, { valueSelector: s => s.responseCount }) || null,
    flaggedCommentsCount: sum(surveys, { valueSelector: s => s.flaggedCommentsCount }) || null,
    firstResponse: surveys.reduce((d, s) => (!d || s.firstResponse?.isBefore(d) ? s.firstResponse : d), null),
    lastResponse: surveys.reduce((d, s) => (!d || s.lastResponse?.isAfter(d) ? s.lastResponse : d), null),
    distributions: surveys.flatMap(s => s.distributions),
    distributionMethods: getDistributionMethods(surveys.flatMap(s => s.distributions), survey.type),
    children: surveys.length > 1 ? surveys : null,
  };
};

const mapSurveyGroup = (group) => {
  const surveys = [
    ...group.surveys.filter(s => s.version && s.primary).map(s => mapSurveyVersion(s, group)),
    ...group.surveys.filter(s => !s.version).map(s => mapSurvey(s, group)),
  ];

  let children;

  if (surveys.length > 1) {
    children = surveys;
  } else if (surveys.length === 1) {
    children = surveys[0].children;
  }

  return {
    id: group.id,
    surveyId: surveys.length > 0 ? surveys[surveys.length - 1].surveyId : null,
    groupId: group.id,
    versions: _.uniq(surveys.map(s => s.version).filter(v => v)),
    primary: !!surveys.find(s => s.primary),
    title: group.title,
    type: group.surveys.length > 0 ? group.surveys[0].type : null,
    languages: uniqueArray(surveys.flatMap(s => s.languages), { prop: 'language' }).sort((a, b) => a.languageName?.localeCompare(b.languageName)),
    viewCount: sum(surveys, { valueSelector: s => s.viewCount }) || null,
    responseCount: sum(surveys, { valueSelector: s => s.responseCount }) || null,
    flaggedCommentsCount: sum(surveys, { valueSelector: s => s.flaggedCommentsCount }) || null,
    firstResponse: surveys.reduce((d, s) => (!d || s.firstResponse?.isBefore(d) ? s.firstResponse : d), null),
    lastResponse: surveys.reduce((d, s) => (!d || s.lastResponse?.isAfter(d) ? s.lastResponse : d), null),
    distributions: surveys.flatMap(s => s.distributions),
    distributionMethods: getDistributionMethods(surveys.flatMap(s => s.distributions), group.surveys[0]?.type),
    children,
    group: true,
    customer: group.customer,
    dataSource: group.dataSource,
  };
};

const Surveys = () => {
  const { message, modal } = useGlobalContext();
  const { loading, updating, data: { surveyGroups = [] } = {}, refetch } = useQuery(GET_SURVEY_GROUPS);
  const [deleteSurvey] = useMutation(DELETE_SURVEY);
  const [search, setSearch] = useState();

  const tableData = useMemo(() => surveyGroups.map(group => mapSurveyGroup(group)), [surveyGroups]);

  const onDeleteSurvey = async (id) => {
    modal.confirm({
      title: 'Are you sure you want to delete this survey?',
      okText: 'Delete',
      okType: 'danger',
      confirmLoading: true,
      onOk: async () => {
        try {
          await deleteSurvey({ variables: { id } });
          refetch();
        } catch (e) {
          message.error(e.message);
        }
      },
    });
  };

  const renderLatestResponse = (lastResponse) => {
    if (!lastResponse) {
      return null;
    }
    const isOld = lastResponse.isBefore(dayjs().subtract(14, 'days'));
    return (
      <Badge status={isOld ? 'warning' : 'success'} text={renderRelativeDate(lastResponse)} />
    );
  };

  const renderFlaggedComments = (count, surveyGroupId) => {
    if (!count) {
      return null;
    }

    return (
      <Link to={`/administration/flagged-comments?surveyGroupId=${surveyGroupId}`}>
        <Badge status="warning" text={<FormattedNumber value={count} />} />
      </Link>
    );
  };

  const renderStatus = (status, record) => (
    <SurveyGroupStatusTag surveyGroup={record} distributions={record.distributions} />
  );

  const columns = useMemo(() => [
    {
      dataIndex: ['customer', 'name'],
      title: <FormattedMessage {...globalMessages.customer} />,
      render: (name, record) => (record.customer ? renderCustomer(record.customer) : null),
    },
    {
      dataIndex: 'title',
      title: <FormattedMessage {...messages.title} />,
      render: (title, record) => (record.dataSource && record.groupId ? <Link to={`/data-sources/${record.dataSource.id}/ux-surveys/results/${record.groupId}`}>{title}</Link> : title),
    },
    {
      dataIndex: 'type',
      title: <FormattedMessage {...globalMessages.type} />,
      render: (type) => <SurveyTypeTag type={type} />,
      filters: Object.values(SurveyType).map(type => ({
        text: <FormattedMessage {...globalMessages[type.toLowerCase()]} />,
        value: type,
      })),
      onFilter: (value, record) => record.type === value,
    },
    getDistributionMethodsColumn(),
    {
      dataIndex: 'version',
      title: <FormattedMessage {...globalMessages.version} />,
      align: 'center',
      render: renderVersion,
    },
    {
      dataIndex: 'languages',
      title: <FormattedMessage {...globalMessages.languages} />,
      render: (languages) => languages.map(l => <LanguageTag key={l.language} {...l} />),
      sorter: arraySorter('languages', 'languageName'),
      search: (languages, s) => languages.find(({ language, languageName }) => (languageName && languageName.toLowerCase().search(s) !== -1) || (language && language.toLowerCase().search(s) !== -1)),
    },
    {
      dataIndex: 'viewCount',
      title: <FormattedMessage {...messages.viewCount} />,
      type: 'number',
    },
    {
      dataIndex: 'responseCount',
      title: <FormattedMessage {...messages.responseCount} />,
      type: 'number',
    },
    {
      dataIndex: 'firstResponse',
      title: <FormattedMessage {...messages.firstResponse} />,
      type: 'relativeDate',
      visible: false,
    },
    {
      dataIndex: 'lastResponse',
      title: <FormattedMessage {...messages.latestResponse} />,
      type: 'datetime',
      render: renderLatestResponse,
      defaultSortOrder: 'descend',
    },
    {
      dataIndex: 'flaggedCommentsCount',
      title: <FormattedMessage {...globalMessages.flaggedComments} />,
      render: (count, record) => renderFlaggedComments(count, record.id),
      type: 'number',
      visible: false,
    },
    {
      dataIndex: 'categories',
      title: <FormattedMessage {...globalMessages.industry} />,
      render: renderTags,
      sorter: arraySorter('categories'),
      search: (categories, s) => categories.find(c => c.title.toLowerCase().search(s) !== -1),
      visible: false,
    },
    {
      dataIndex: 'status',
      title: <FormattedMessage {...globalMessages.status} />,
      render: renderStatus,
      align: 'center',
      width: 100,
      sorter: false,
    },
  ], []);

  const actionsMenuItems = ({ id, firstResponse }) => [
    { key: 'delete-survey', label: <FormattedMessage {...globalMessages.delete} />, icon: <DeleteFilled />, danger: true, onClick: () => onDeleteSurvey(id), disabled: !!firstResponse },
  ];

  return (
    <Page>
      <Page.Header
        title={<FormattedMessage {...globalMessages.surveys} />}
        extra={(
          <Input.Search onSearch={setSearch} allowClear />
        )}
      />
      <Page.Content>
        <Table
          loading={loading}
          emptyMessage={<FormattedMessage {...messages.emptyMsg} />}
          columns={columns}
          dataSource={tableData}
          search={search}
          actionsMenuItems={actionsMenuItems}
          footerActions={updating ? (
            <Space>
              <LoadingOutlined />
              Updating...
            </Space>
          ) : null}
        />
      </Page.Content>
    </Page>
  );
};

export default Surveys;
